import React, { useState, useContext } from "react"
import { ThemeProvider, createTheme, styled } from "@mui/material/styles"
import { Box } from "@mui/material"
import { useNavigate } from "react-router-dom"
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import ModalImportCSV from "../../components/CustomCSVImportButton"
import { GlobalContext } from "../../contexts/GlobalContext"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"

// Importaciones para las acciones dinámicas, mutaciones y exportaciones
import { useQueryClient } from "@tanstack/react-query"
import { useMutation, api } from "../../api"
import getIconComponent from "../utils/getIconComponent"
import CustomBackdrop from "../../components/CustomBackdrop"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"

// Estilos base para el layout principal
const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

// Tema estándar de SIACDEV1.0
const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const CentrosPPE = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Estado para controlar la apertura del modal de importación
  const [openImportModal, setOpenImportModal] = useState(false)

  // Mutación para eliminación con recarga de grilla
  const { mutateAsync: SaveEliminacionCentroActivo, isPending: isDeletingCentroActivo } = useMutation({
    queryKey: ["isDeletingCentroActivo"],
    fn: async (data) => {
      // Endpoint de eliminación en nuestro backend
      const response = await api.post("/CentrosPPE/eliminarCentrosPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["CentrosPPE"] })
    },
  })

  // Columnas a mostrar en la tabla principal
  const columnsTable = [
    { accessorKey: "centcodigo", header: "Código Centro", size: 120 },
    { accessorKey: "centdescri", header: "Descripción", size: 300 },
    {
      accessorKey: "centstatus",
      header: "Estado",
      size: 120,
      Cell: ({ cell }) => (cell.getValue() === "A" ? "ACTIVO" : "INACTIVO"),
    },
    {
      accessorKey: "centfecisys",
      header: "Fecha de Creación",
      size: 200,
      Cell: ({ cell }) => {
        const value = cell.getValue()
        return <span>{normalFormatDate(value)}</span>
      },
    },
    { accessorKey: "centusuisys", header: "Creado Por", size: 150 },
    {
      accessorKey: "centfecmsys",
      header: "Fecha de Modificación",
      size: 200,
      Cell: ({ cell }) => {
        const value = cell.getValue()
        return <span>{normalFormatDate(value)}</span>
      },
    },
    { accessorKey: "centusumsys", header: "Modificado Por", size: 150 },
  ]

  // Configuración de validación para el CSV (columnas clave según la tabla afbcentro)
  const fieldConfigs = {
    centcodigo: { required: true, key: true },
    centdescri: { required: true },
    centstatus: { required: false }, // Es opcional porque el backend lo asume como 'A' por defecto
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Gestión de Centros de PPE</b>
        </div>

        <CustomBackdrop isLoading={isDeletingCentroActivo} />

        <StyledRoot>
          {/* Modal de Importación CSV integrado */}
          <ModalImportCSV
            open={openImportModal}
            onClose={() => setOpenImportModal(false)}
            templateFileName="Plantilla_CentrosPPE.csv"
            fieldConfigs={fieldConfigs}
            maxFileSize={10 * 1024 * 1024}
            // Endpoints que apuntan a nuestro nuevo backend
            validateEndpoint="/CentrosPPE/validarCentrosPPEIMP"
            insertEndpoint="/CentrosPPE/insertarCentrosPPEIMP"
            onImportComplete={() => qc.invalidateQueries({ queryKey: ["CentrosPPE"] })}
          />

          <CustomConditionalActionsTableServer
            endpoint="/CentrosPPE/getAllCentrosPPE"
            errorMsgFilterSearch="Error al cargar datos de Centros"
            queryKeyModal="CentrosPPE"
            perPage={10}
            rowActionsWidthTable={120}
            columnsTable={columnsTable}
            // ACCIONES DE FILA (Editar y Eliminar)
            rowActions={(row) => {
              const editarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "EDITAR",
              )
              const eliminarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "ELIMINAR",
              )

              const actions = [
                {
                  label: editarAction?.acccaption || "Editar",
                  key: editarAction?.acccaption || "EDITAR",
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  onClick: (row) => {
                    navigate("editar", { state: row.original })
                  },
                },
                {
                  label: eliminarAction?.acccaption || "Eliminar",
                  key: eliminarAction?.acccaption || "ELIMINAR",
                  icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                  onClick: async (row) => {
                    try {
                      await SaveEliminacionCentroActivo(row.original)
                    } catch (err) {
                      console.error("Error eliminando el Centro de activo:", err)
                    }
                  },
                },
              ]
              return actions
            }}
            // BARRA DE HERRAMIENTAS SUPERIOR (Crear, Exportar, Importar)
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")
              const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "EXPORTAR",
              )

              const toolbarActions = [
                {
                  label: crearAction?.acccaption || "Crear",
                  key: crearAction?.acccaption || "CREAR",
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => {
                    navigate("crear")
                  },
                },
                {
                  type: "dropdown",
                  label: exportarAction?.acccaption || "Exportar",
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ columns, data }) => {
                        if (device === "sm") {
                          return handleExportDataPdfSMScreen(
                            columns,
                            data,
                            "Reporte de Centros de PPE",
                            `Reporte_Centros_PPE_${new Date().toLocaleString()}`,
                          )
                        }
                        handleExportDataPdfLGScreen(
                          columns,
                          table.getCoreRowModel().rows,
                          "Reporte de Centros de PPE",
                          `Reporte_Centros_PPE_${new Date().toLocaleString()}`,
                        )
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ data }) => {
                        handleAllExportDataCSV(data, `Reporte_Centros_PPE_${new Date().toLocaleString()}`)
                      },
                    },
                  ],
                },
                {
                  label: "Importar",
                  key: "importarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  onClick: () => {
                    setOpenImportModal(true)
                  },
                },
              ]
              return toolbarActions
            }}
          />
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default CentrosPPE
